/** Supplier Profile detail screen (Demo Mode only, local-only - no backend persistence
 * yet). Full supplier control record: header summary + tabs covering Overview, Legal &
 * Commercial, Contacts, Coverage & Sites, Catalogues, Documents & Certifications,
 * Performance, Contracts & Renewals, Exceptions and Activity. Reuses the existing
 * governance profile (certifications/insurance/performance - see
 * `supplierGovernanceData.ts`) plus the additional record types in
 * `supplierProfileData.ts`. Reached from the Suppliers list row click while Demo Mode is
 * enabled (see `SupplierItemRow.tsx`); the standard Supplier edit form is unaffected and
 * remains the production CRUD screen for suppliers. */

import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Chip, Stack, Tab, Tabs,
} from '@mui/material';
import PageArea from '../../../../components/shared/PageArea';
import PageTitle from '../../../../components/shared/PageTitle';
import BackButton from '../../../../components/shared/actions/BackButton';
import { getSupplierGovernanceProfile } from '../../../../mocks/data/supplierGovernanceData';
import {
  getSupplierDirectoryEntry,
  getSupplierCertificationStatus,
  getSupplierCoverageAssignments,
  getSupplierCatalogues,
  getSupplierExceptions,
  SupplierOperationalStatus,
} from '../../../../mocks/data/supplierProfileData';
import OverviewTab from './components/OverviewTab';
import LegalCommercialTab from './components/LegalCommercialTab';
import ContactsTab from './components/ContactsTab';
import CoverageSitesTab from './components/CoverageSitesTab';
import CataloguesTab from './components/CataloguesTab';
import DocumentsCertificationsTab from './components/DocumentsCertificationsTab';
import PerformanceTab from './components/PerformanceTab';
import ContractsRenewalsTab from './components/ContractsRenewalsTab';
import ExceptionsTab from './components/ExceptionsTab';
import ActivityTab from './components/ActivityTab';

enum SupplierProfileTab {
  Overview = 'Overview',
  LegalCommercial = 'LegalCommercial',
  Contacts = 'Contacts',
  CoverageSites = 'CoverageSites',
  Catalogues = 'Catalogues',
  Documents = 'Documents',
  Performance = 'Performance',
  ContractsRenewals = 'ContractsRenewals',
  Exceptions = 'Exceptions',
  Activity = 'Activity',
}

const OPERATIONAL_STATUS_COLOR: Record<SupplierOperationalStatus, 'success' | 'warning' | 'error'> = {
  [SupplierOperationalStatus.Active]: 'success',
  [SupplierOperationalStatus.OnHold]: 'warning',
  [SupplierOperationalStatus.Suspended]: 'error',
};

const CERTIFICATION_STATUS_COLOR: Record<string, 'success' | 'warning' | 'error'> = {
  Compliant: 'success',
  ActionNeeded: 'warning',
  Expired: 'error',
};

const SupplierProfilePage = () => {
  const { t } = useTranslation('suppliers');
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const supplierId = Number(id);
  const [tabValue, setTabValue] = useState<SupplierProfileTab>(SupplierProfileTab.Overview);

  const directoryEntry = useMemo(() => getSupplierDirectoryEntry(supplierId), [supplierId]);
  const governanceProfile = useMemo(() => getSupplierGovernanceProfile(supplierId), [supplierId]);
  const certificationStatus = useMemo(() => getSupplierCertificationStatus(supplierId), [supplierId]);
  const coverageAssignments = useMemo(() => getSupplierCoverageAssignments(supplierId), [supplierId]);
  const catalogues = useMemo(() => getSupplierCatalogues(supplierId), [supplierId]);
  const exceptions = useMemo(() => getSupplierExceptions(supplierId), [supplierId]);

  const assignedSitesCount = useMemo(() => {
    const siteAssignments = coverageAssignments.filter((assignment) => assignment.level === 'IndividualSite').length;
    return siteAssignments || coverageAssignments.length;
  }, [coverageAssignments]);
  const activeCataloguesCount = catalogues.filter((catalogue) => catalogue.status === 'Active').length;
  const openExceptionsCount = exceptions.filter((exception) => exception.status === 'Open' || exception.status === 'InProgress').length;

  return (
    <PageArea>
      <BackButton
        handleClick={() => navigate('/procurement/suppliers')}
        title={t('backToSuppliers', 'Back to Suppliers')}
        isLink={false}
        styles={{ marginBottom: '20px' }}
      />

      <PageTitle>{directoryEntry.supplierName}</PageTitle>

      <Stack direction="row"
        flexWrap="wrap"
        spacing={2}
        sx={{ mt: 3, mb: 5 }}>
        <Chip
          label={`${t('approvalStatus', 'Approval status')}: ${governanceProfile.approvalStatus}`}
          color={governanceProfile.approvalStatus === 'Approved' || governanceProfile.approvalStatus === 'AutoApprovedLowVolume' ? 'success' : governanceProfile.approvalStatus === 'PendingReview' ? 'warning' : 'error'}
          size="small"
        />
        <Chip
          label={`${t('operationalStatus', 'Operational status')}: ${directoryEntry.operationalStatus}`}
          color={OPERATIONAL_STATUS_COLOR[directoryEntry.operationalStatus]}
          size="small"
        />
        <Chip
          label={`${t('certificationStatus', 'Certification status')}: ${certificationStatus}`}
          color={CERTIFICATION_STATUS_COLOR[certificationStatus]}
          size="small"
        />
        <Chip
          label={`${t('openExceptions', 'Open exceptions')}: ${openExceptionsCount}`}
          color={openExceptionsCount > 0 ? 'error' : 'default'}
          size="small"
          variant={openExceptionsCount > 0 ? 'filled' : 'outlined'}
        />
        <Chip label={`${t('assignedSites', 'Assigned sites')}: ${assignedSitesCount}`}
          size="small"
          variant="outlined" />
        <Chip label={`${t('activeCatalogues', 'Active catalogues')}: ${activeCataloguesCount}`}
          size="small"
          variant="outlined" />
      </Stack>

      <Tabs
        value={tabValue}
        onChange={(_, value) => setTabValue(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 4, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label={t('tabOverview', 'Overview')}
          value={SupplierProfileTab.Overview} />
        <Tab label={t('tabLegalCommercial', 'Legal & Commercial')}
          value={SupplierProfileTab.LegalCommercial} />
        <Tab label={t('tabContacts', 'Contacts')}
          value={SupplierProfileTab.Contacts} />
        <Tab label={t('tabCoverageSites', 'Coverage & Sites')}
          value={SupplierProfileTab.CoverageSites} />
        <Tab label={t('tabCatalogues', 'Catalogues')}
          value={SupplierProfileTab.Catalogues} />
        <Tab label={t('tabDocuments', 'Documents & Certifications')}
          value={SupplierProfileTab.Documents} />
        <Tab label={t('tabPerformance', 'Performance')}
          value={SupplierProfileTab.Performance} />
        <Tab label={t('tabContractsRenewals', 'Contracts & Renewals')}
          value={SupplierProfileTab.ContractsRenewals} />
        <Tab label={(
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {t('tabExceptions', 'Exceptions')}
            {openExceptionsCount > 0 && (
              <Chip label={openExceptionsCount}
                color="error"
                size="small" />
            )}
          </Box>
        )}
        value={SupplierProfileTab.Exceptions} />
        <Tab label={t('tabActivity', 'Activity')}
          value={SupplierProfileTab.Activity} />
      </Tabs>

      <Box>
        {tabValue === SupplierProfileTab.Overview && (
          <OverviewTab
            directoryEntry={directoryEntry}
            assignedSitesCount={assignedSitesCount}
            activeCataloguesCount={activeCataloguesCount}
            openExceptionsCount={openExceptionsCount}
            certificationStatus={certificationStatus}
          />
        )}
        {tabValue === SupplierProfileTab.LegalCommercial && <LegalCommercialTab supplierId={supplierId} />}
        {tabValue === SupplierProfileTab.Contacts && <ContactsTab supplierId={supplierId} />}
        {tabValue === SupplierProfileTab.CoverageSites && <CoverageSitesTab supplierId={supplierId} />}
        {tabValue === SupplierProfileTab.Catalogues && <CataloguesTab supplierId={supplierId} />}
        {tabValue === SupplierProfileTab.Documents && <DocumentsCertificationsTab supplierId={supplierId} />}
        {tabValue === SupplierProfileTab.Performance && <PerformanceTab governanceProfile={governanceProfile} />}
        {tabValue === SupplierProfileTab.ContractsRenewals && <ContractsRenewalsTab supplierId={supplierId} />}
        {tabValue === SupplierProfileTab.Exceptions && <ExceptionsTab supplierId={supplierId} />}
        {tabValue === SupplierProfileTab.Activity && <ActivityTab supplierId={supplierId} />}
      </Box>
    </PageArea>
  );
};

export default SupplierProfilePage;
